import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  // Auto-advance gallery slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev === slideshowImages.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-advance hero slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev === heroSlides.length - 1 ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const heroSlides = [
    {
      url: "https://public.readdy.ai/ai/img_res/d89b01a6edf596c82a8d0acfa1b331e9.jpg",
      title: "Modern Kitchen Renovation"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Premium%20contemporary%20kitchen%20design%20with%20dark%20navy%20cabinets%20marble%20countertops%20gold%20hardware%20modern%20appliances%20open%20shelving%20natural%20wood%20accents%20bright%20airy%20space%20professional%20renovation%20simple%20clean%20background&width=1920&height=1080&seq=hero-kitchen-new-2&orientation=landscape",
      title: "Premium Kitchen Design"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Luxurious%20modern%20bathroom%20renovation%20with%20white%20marble%20tiles%20rainfall%20shower%20glass%20enclosure%20floating%20double%20vanity%20chrome%20fixtures%20LED%20mirror%20and%20spa-like%20atmosphere%20clean%20bright%20natural%20lighting%20simple%20elegant%20background&width=1920&height=1080&seq=hero-bathroom-1&orientation=landscape",
      title: "Luxury Bathroom Renovation"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Beautiful%20luxury%20vinyl%20tile%20flooring%20installation%20in%20modern%20kitchen%20with%20grey%20wood-look%20planks%20contemporary%20cabinets%20stainless%20appliances%20natural%20lighting%20clean%20professional%20finish%20simple%20background&width=1920&height=1080&seq=hero-flooring-2&orientation=landscape",
      title: "Premium Flooring Installation"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Freshly%20painted%20modern%20interior%20living%20space%20with%20smooth%20sage%20green%20feature%20wall%20white%20trim%20crisp%20clean%20lines%20contemporary%20furniture%20natural%20sunlight%20professional%20finish%20minimalist%20elegant%20background&width=1920&height=1080&seq=hero-paint-1&orientation=landscape",
      title: "Professional Interior Painting"
    }
  ];

  const slideshowImages = [
    {
      url: "https://readdy.ai/api/search-image?query=Stunning%20modern%20hardwood%20flooring%20installation%20in%20spacious%20living%20room%20with%20rich%20walnut%20planks%20natural%20wood%20grain%20patterns%20elegant%20furniture%20warm%20ambient%20lighting%20and%20minimalist%20contemporary%20interior%20design%20simple%20clean%20background&width=1400&height=800&seq=slideshow-flooring-1&orientation=landscape",
      title: "Premium Hardwood Flooring",
      category: "Flooring Installation"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Luxurious%20modern%20bathroom%20renovation%20with%20white%20marble%20tiles%20rainfall%20shower%20glass%20enclosure%20floating%20double%20vanity%20chrome%20fixtures%20LED%20mirror%20and%20spa-like%20atmosphere%20clean%20bright%20natural%20lighting%20simple%20elegant%20background&width=1400&height=800&seq=slideshow-bathroom-1&orientation=landscape",
      title: "Luxury Bathroom Renovation",
      category: "Bathroom Restoration"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Freshly%20painted%20modern%20interior%20living%20space%20with%20smooth%20sage%20green%20feature%20wall%20white%20trim%20crisp%20clean%20lines%20contemporary%20furniture%20natural%20sunlight%20professional%20finish%20minimalist%20elegant%20background&width=1400&height=800&seq=slideshow-paint-1&orientation=landscape",
      title: "Professional Interior Painting",
      category: "Painting Services"
    },
    {
      url: "https://i.imgur.com/rDWKm1I.jpeg",
      title: "Luxury Vinyl Installation",
      category: "Flooring Installation"
    },
    {
      url: "https://readdy.ai/api/search-image?query=Elegant%20bathroom%20restoration%20with%20subway%20tile%20walls%20freestanding%20bathtub%20modern%20fixtures%20brushed%20nickel%20hardware%20natural%20stone%20accents%20warm%20lighting%20spa%20atmosphere%20simple%20clean%20background&width=1400&height=800&seq=slideshow-bathroom-2&orientation=landscape",
      title: "Complete Bathroom Transformation",
      category: "Bathroom Restoration"
    }
  ];

  const testimonials = [
    {
      rating: 4.9,
      text: "Shebaro Renovation Services transformed our outdated bathroom into a stunning modern sanctuary. Their attention to detail and professionalism exceeded all expectations. The team was punctual, clean, and respectful of our home throughout the entire process.",
      name: "Sarah Mitchell",
      project: "Bathroom Renovation, North London",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20woman%20in%20her%2040s%20with%20blonde%20hair%20wearing%20casual%20business%20attire%20standing%20in%20a%20modern%20renovated%20home%20interior%20with%20natural%20lighting%20and%20contemporary%20design%20elements&width=120&height=120&seq=testimonial-avatar-1&orientation=squarish"
    },
    {
      rating: 5.0,
      text: "We hired Shebaro for complete flooring installation throughout our three-bedroom home. The craftsmanship was impeccable, and they completed the project ahead of schedule. The hardwood floors look absolutely gorgeous and have completely transformed our living space.",
      name: "Michael Thompson",
      project: "Hardwood Flooring Installation, West London",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20man%20in%20his%2050s%20with%20dark%20hair%20wearing%20casual%20shirt%20standing%20in%20a%20beautifully%20renovated%20home%20with%20hardwood%20floors%20and%20modern%20interior%20design%20natural%20lighting&width=120&height=120&seq=testimonial-avatar-2&orientation=squarish"
    },
    {
      rating: 4.8,
      text: "The painting service provided by Shebaro was exceptional. They helped us choose the perfect color palette and executed the work flawlessly. Our home feels brand new, and we've received countless compliments from friends and family about the transformation.",
      name: "Emma Roberts",
      project: "Interior Painting, East London",
      avatar: "https://readdy.ai/api/search-image?query=Professional%20woman%20in%20her%2030s%20with%20brown%20hair%20smiling%20standing%20in%20a%20freshly%20painted%20modern%20home%20interior%20with%20bright%20natural%20light%20and%20contemporary%20furniture&width=120&height=120&seq=testimonial-avatar-3&orientation=squarish"
    }
  ];

  const galleryProjects = [
    {
      title: "Modern Basement Renovation",
      category: "Flooring Installation",
      year: "2024",
      image: "https://static.readdy.ai/image/3bebbbc297a21c2099cba4735b27cf60/13ba4fecfdce018941ec6408b376e23a.jpeg",
      bg: "bg-gray-50"
    },
    {
      title: "Modern Bathroom Suite",
      category: "Bathroom Renovation",
      year: "2024",
      image: "https://readdy.ai/api/search-image?query=Luxurious%20modern%20bathroom%20renovation%20with%20white%20marble%20tiles%20large%20walk-in%20shower%20glass%20enclosure%20floating%20vanity%20with%20vessel%20sink%20chrome%20fixtures%20LED%20mirror%20and%20minimalist%20design%20clean%20bright%20natural%20lighting&width=800&height=600&seq=gallery-bathroom-1&orientation=landscape",
      bg: "bg-blue-50"
    },
    {
      title: "Contemporary Interior Paint",
      category: "Professional Painting",
      year: "2024",
      image: "https://readdy.ai/api/search-image?query=Freshly%20painted%20modern%20interior%20room%20with%20smooth%20sage%20green%20accent%20wall%20white%20trim%20crisp%20clean%20lines%20contemporary%20furniture%20natural%20lighting%20professional%20finish%20simple%20minimalist%20background&width=800&height=600&seq=gallery-paint-1&orientation=landscape",
      bg: "bg-gray-50"
    }
  ];

  const services = [
    {
      icon: "ri-layout-grid-line",
      title: "Premium Flooring",
      description: "Expert installation of hardwood, laminate, tile, and luxury vinyl flooring. We work with premium materials to create beautiful, durable floors that enhance your space and stand the test of time."
    },
    {
      icon: "ri-paint-brush-line",
      title: "Professional Painting",
      description: "Interior painting services with meticulous preparation and flawless execution. Our color consultation ensures your vision comes to life with stunning results that transform your property."
    },
    {
      icon: "ri-drop-line",
      title: "Bathroom Restoration",
      description: "Complete bathroom renovations from design to installation. We create modern, functional spaces with high-quality fixtures, elegant tilework, and attention to every detail for lasting beauty."
    }
  ];

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      alert('Message must be 500 characters or less');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5nr3ovecauet1j8b9ig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/3bebbbc297a21c2099cba4735b27cf60/2d939d11fbc69a5542cf9f5e56ca6f39.png" 
                alt="Shebaro Renovation Services LTD" 
                className="h-14 w-auto object-contain"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-[#6B7F6E]' : 'text-white hover:text-gray-200'}`}>Home</a>
              <a href="#about" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-[#6B7F6E]' : 'text-white hover:text-gray-200'}`}>About Us</a>
              <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-[#6B7F6E]' : 'text-white hover:text-gray-200'}`}>Services</a>
              <a href="#gallery" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-[#6B7F6E]' : 'text-white hover:text-gray-200'}`}>Gallery</a>
              <a href="#contact" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-[#6B7F6E]' : 'text-white hover:text-gray-200'}`}>Contact Us</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-6 py-4 space-y-3">
              <a href="#home" className="block text-sm font-medium text-gray-900 hover:text-[#6B7F6E] cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Home</a>
              <a href="#about" className="block text-sm font-medium text-gray-900 hover:text-[#6B7F6E] cursor-pointer" onClick={() => setMobileMenuOpen(false)}>About Us</a>
              <a href="#services" className="block text-sm font-medium text-gray-900 hover:text-[#6B7F6E] cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#gallery" className="block text-sm font-medium text-gray-900 hover:text-[#6B7F6E] cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
              <a href="#contact" className="block text-sm font-medium text-gray-900 hover:text-[#6B7F6E] cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Contact Us</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Slideshow with Overlay */}
        <div className="absolute inset-0 w-full h-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentHeroSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.url}
                alt={slide.title}
                className="w-full h-full object-cover object-center"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left Content - 60% */}
            <div className="w-full lg:w-3/5">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-8">
                <i className="ri-home-smile-line text-white text-lg"></i>
                <span className="text-white text-sm font-medium whitespace-nowrap">Est. 2020</span>
              </div>
              
              <h1 className="text-6xl lg:text-8xl font-extrabold text-white leading-tight mb-20">
                TRANSFORM<br />YOUR SPACE<br />WITH EXPERT<br />RENOVATION
              </h1>
            </div>

            {/* Right Content - 40% */}
            <div className="w-full lg:w-2/5 space-y-8">
              <p className="text-base text-white/90 leading-relaxed">
                Shebaro Renovation Services specializes in premium flooring installation, professional painting, and complete bathroom restoration. We bring decades of expertise and uncompromising quality to every project, transforming your vision into reality.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a 
                  href="#contact" 
                  className="px-8 py-4 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Free Quote
                </a>
                <a 
                  href="#gallery" 
                  className="px-8 py-4 bg-transparent text-white text-sm font-semibold border-b-2 border-white hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Gallery
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-20">
            <p className="text-sm font-semibold text-[#6B7F6E] uppercase tracking-wider mb-4">OUR EXPERTISE</p>
            <h2 className="text-5xl lg:text-6xl font-extrabold text-[#1A1A1A] leading-tight">
              SPECIALIZED<br />RENOVATION SERVICES
            </h2>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-[#EFEBE6] rounded-2xl p-12 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-5xl text-[#1A1A1A]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">{service.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs font-semibold text-[#6B7F6E] uppercase tracking-wider mb-6">ABOUT SHEBARO</p>
            
            <h2 className="text-5xl lg:text-7xl font-bold text-[#1A1A1A] leading-tight mb-12">
              WE BRING YOUR<br />VISION TO LIFE
            </h2>

            <div className="space-y-6 mb-12 text-base text-gray-700 leading-relaxed">
              <p>
                Shebaro Renovation Services LTD is your trusted partner for transforming residential and commercial spaces across London. With years of experience and a commitment to excellence, we specialize in three core areas: premium flooring installation, professional painting services, and complete bathroom restoration.
              </p>
              <p>
                Our team of skilled craftsmen combines traditional expertise with modern techniques to deliver outstanding results. We understand that your property is a significant investment, which is why we treat every project with the utmost care and attention to detail. From initial consultation to final walkthrough, we ensure a seamless renovation experience.
              </p>
              <p>
                What sets us apart is our dedication to quality materials, transparent communication, and timely project completion. We work closely with each client to understand their unique vision and budget, providing expert guidance and creative solutions. Whether you're updating a single room or renovating your entire property, Shebaro Renovation Services delivers exceptional craftsmanship that exceeds expectations.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#contact" 
                className="px-8 py-4 bg-[#1A1A1A] text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Schedule Consultation
              </a>
              <a 
                href="#services" 
                className="px-8 py-4 bg-transparent text-[#1A1A1A] text-sm font-semibold rounded-full border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                Our Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section with Slideshow */}
      <section id="gallery" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">OUR WORK</p>
            <h2 className="text-5xl font-bold text-black">RECENT PROJECTS</h2>
          </div>

          {/* Slideshow */}
          <div className="relative w-full h-[600px] rounded-3xl overflow-hidden mb-16 shadow-xl">
            {/* Images */}
            {slideshowImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.title}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                
                {/* Image Info */}
                <div className="absolute bottom-0 left-0 right-0 p-12">
                  <p className="text-sm font-semibold text-white/80 uppercase tracking-wider mb-2">{image.category}</p>
                  <h3 className="text-4xl font-bold text-white">{image.title}</h3>
                </div>
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={() => setCurrentSlide((prev) => (prev === 0 ? slideshowImages.length - 1 : prev - 1))}
              className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg cursor-pointer z-10"
            >
              <i className="ri-arrow-left-s-line text-2xl text-gray-900"></i>
            </button>
            <button
              onClick={() => setCurrentSlide((prev) => (prev === slideshowImages.length - 1 ? 0 : prev + 1))}
              className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all shadow-lg cursor-pointer z-10"
            >
              <i className="ri-arrow-right-s-line text-2xl text-gray-900"></i>
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3 z-10">
              {slideshowImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'w-12 h-3 bg-white rounded-full'
                      : 'w-3 h-3 bg-white/50 rounded-full hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {galleryProjects.map((project, index) => (
              <div key={index} className={`${project.bg} rounded-3xl p-6 shadow-sm hover:shadow-xl transition-shadow`}>
                <div className="w-full h-80 rounded-2xl overflow-hidden mb-6">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <p className="text-lg font-medium text-center text-gray-900 mb-2">{project.category}</p>
                <h3 className="text-2xl font-bold text-center text-black mb-6">{project.title}</h3>
                <div className="flex justify-center mb-3">
                  <button className={`w-4/5 py-3 text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer ${index === 0 ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black border border-black hover:bg-gray-50'}`}>
                    View Project
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-center">Completed {project.year}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center text-gray-900 mb-20">(WHAT OUR CLIENTS SAY)</h2>

          <div className="bg-white rounded-3xl p-12 shadow-sm">
            <div className="flex items-center gap-2 mb-8">
              <div className="bg-[#1A1A1A] text-white px-4 py-2 rounded-full flex items-center gap-2">
                <i className="ri-star-fill text-orange-400"></i>
                <span className="font-bold">{testimonials[currentTestimonial].rating}</span>
              </div>
            </div>

            <blockquote className="text-xl font-serif text-gray-800 leading-relaxed mb-10">
              "{testimonials[currentTestimonial].text}"
            </blockquote>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <img 
                    src={testimonials[currentTestimonial].avatar} 
                    alt={testimonials[currentTestimonial].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{testimonials[currentTestimonial].name}</p>
                  <p className="text-sm text-gray-500">{testimonials[currentTestimonial].project}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1))}
                  className="w-12 h-12 bg-white border border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
                </button>
                <button 
                  onClick={() => setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1))}
                  className="w-12 h-12 bg-black rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl text-white"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-black mb-6">READY TO START YOUR PROJECT?</h2>
          <p className="text-lg text-gray-600 mb-10">Get a free consultation and quote today</p>
          
          <a 
            href="#contact-form" 
            className="inline-flex items-center gap-3 px-12 py-5 bg-[#5C4033] text-white text-lg font-semibold rounded-full hover:bg-[#4A3329] transition-colors cursor-pointer whitespace-nowrap"
          >
            Contact Us Now
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-24 bg-[#F8F6F3]">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-[#1A1A1A] mb-4">Get In Touch</h3>
            <p className="text-base text-gray-600">Fill out the form below and we'll get back to you within 24 hours</p>
          </div>

          <form id="contact-form-main" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6 bg-white rounded-2xl p-10 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-900 mb-2">First Name *</label>
                <input 
                  type="text" 
                  id="firstName" 
                  name="firstName" 
                  required 
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-900 mb-2">Last Name *</label>
                <input 
                  type="text" 
                  id="lastName" 
                  name="lastName" 
                  required 
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email Address *</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                required 
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">Phone Number</label>
              <input 
                type="tel" 
                id="phone" 
                name="phone" 
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">Service Interested In *</label>
              <select 
                id="service" 
                name="service" 
                required 
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent cursor-pointer"
              >
                <option value="">Select a service</option>
                <option value="flooring">Premium Flooring</option>
                <option value="painting">Professional Painting</option>
                <option value="bathroom">Bathroom Restoration</option>
                <option value="multiple">Multiple Services</option>
                <option value="consultation">General Consultation</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">Project Details *</label>
              <textarea 
                id="message" 
                name="message" 
                rows={5} 
                required 
                maxLength={500}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B7F6E] focus:border-transparent resize-none"
                placeholder="Tell us about your project..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
            </div>

            {formStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 text-center font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
              </div>
            )}

            {formStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 text-center font-medium">Something went wrong. Please try again.</p>
              </div>
            )}

            <button 
              type="submit" 
              disabled={formStatus === 'submitting'}
              className="w-full py-4 bg-[#1A1A1A] text-white text-base font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
            {/* Left Column - Brand */}
            <div>
              <h3 className="text-5xl font-extrabold mb-8">SHEBARO</h3>
              <p className="text-base text-white/80 leading-relaxed mb-10 max-w-xs">
                Expert renovation services specializing in flooring, painting, and bathroom restoration. Transforming spaces with quality craftsmanship and attention to detail.
              </p>
              <div className="flex gap-5">
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-white hover:bg-white/10 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
              </div>
            </div>

            {/* Middle Column - Contact Info */}
            <div className="space-y-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3 text-white/60">ADDRESS</p>
                <p className="text-base text-white">6579 Skipper way, Mississauga, ON</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3 text-white/60">PHONE</p>
                <p className="text-base text-white">+1 (647) 333-3512</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3 text-white/60">EMAIL</p>
                <p className="text-base text-white">support@shbaroreno.com</p>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className="w-full h-72 rounded-xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20renovation%20team%20member%20or%20craftsman%20working%20on%20quality%20home%20improvement%20project%20wearing%20work%20clothes%20holding%20tools%20with%20warm%20natural%20lighting%20and%20simple%20clean%20background%20showcasing%20expertise%20and%20professionalism&width=600&height=800&seq=footer-team-1&orientation=portrait"
                alt="Team Member"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="pt-6 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-white/80">© 2024 Shebaro Renovation Services LTD. All rights reserved.</p>
              <div className="flex gap-6">
                <a href="#" className="text-xs text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</a>
                <span className="text-white/40">|</span>
                <a href="#" className="text-xs text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Terms</a>
                <span className="text-white/40">|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
